import {
  ServerStackIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { MultiSelect } from '../multi-select';
import { ProviderImage } from '../provider-image';
import { SkeletonLoader } from '../skeleton-loader';
import { Tooltip } from '../tooltip';
import { catalogProvider } from '../../constants/code-provider';
import { generatePublicImagePath } from '../static-image/generatePublicImagePath';
import {
  FilterControls,
  IntegrationFilter,
  ResourceFilter,
} from './filter-types';

const MAX_TEXT_LENGTH = 50;
const RESOURCE_TYPE_DROPDOWN_WIDTH = 400;

// Parses a resource type string of the form "Provider::Service::Resource"
function parseResourceType(resourceType: string) {
  const parts = resourceType.split('::');
  if (parts.length !== 3) {
    return { provider: 'unknown', service: 'unknown', resource: resourceType };
  }
  const [provider, service, resource] = parts;
  return { provider, service, resource };
}

// Builds a basic search string for a resource type
function buildSearchString(
  provider: string,
  service: string,
  resource: string,
  original: string,
): string {
  return `${provider} ${service} ${resource} ${service} ${original}`;
}

export const ResourceTypeFilterFC = ({
  loading,
  error,
  label,
  value,
  data,
  onClose,
  filters,
  setValue,
  filteredValues,
  placement,
  displayMaxItems,
  selectText,
  permanent,
  showInlineClear,
  hideIcon,
  disabled = false,
  showOperator,
  wrapperClassName,
  dataTestId,
}: ResourceFilter & FilterControls) => {
  const provider = filters.find((filter) => filter.type === 'provider')?.id;
  const providerValues =
    provider && filteredValues
      ? ((filteredValues[provider] as string[]) || []).map((value) =>
          catalogProvider(value),
        )
      : [];
  const integration = filters.find(
    (filter) => filter.type === 'integrationId',
  ) as IntegrationFilter | undefined;
  const integrationProviderValues =
    integration && filteredValues
      ? ((filteredValues[integration.id] as string[]) || []).map((x) => {
          const selected = integration.data?.find(
            (int) => int.integrationId === x,
          );
          return catalogProvider(selected?.provider, selected?.category);
        })
      : [];
  const transformedResourceType =
    data?.map((type) => {
      const { provider, service, resource } = parseResourceType(type);
      return {
        value: type,
        provider,
        service,
        resource,
        // A cloud type shows its provider's logo; a code type, whose formats
        // differ more than their hosts do, shows its own icon.
        image:
          provider === 'Code'
            ? generatePublicImagePath({ type: 'resourceType', name: type })
            : undefined,
      };
    }) || [];
  return (
    <SkeletonLoader
      status={loading ? 'loading' : error ? 'error' : 'success'}
      error={error}
      type="line"
    >
      <MultiSelect
        dataTestId={dataTestId}
        label={label}
        disabled={disabled}
        showOperator={showOperator}
        displayButtonClassName={wrapperClassName}
        options={
          transformedResourceType
            .filter((type) => {
              return (
                (!providerValues.length && !integrationProviderValues.length) ||
                providerValues.includes(type.provider) ||
                integrationProviderValues.includes(type.provider) ||
                value.includes(type.value)
              );
            })
            .map((type) => {
              const labelText = `${type.service} ${type.resource}`;
              const isTextTruncated = labelText.length > MAX_TEXT_LENGTH;
              return {
                value: type.value,
                searchString: buildSearchString(
                  type.provider,
                  type.service,
                  type.resource,
                  type.value,
                ),
                label: (
                  <div className="flex flex-col truncate">
                    <div className="flex items-center gap-1">
                      <ProviderImage
                        sizeClassName="w-8 h-6 flex-shrink-0"
                        provider={type.provider}
                        img={type.image}
                      />
                      <div className="mr-1 truncate">{labelText}</div>
                      {isTextTruncated && (
                        <Tooltip
                          label={type.value || ''}
                          style={{
                            maxWidth: '500px',
                          }}
                        >
                          <InformationCircleIcon className="h-5 w-5 flex-shrink-0 text-gray-600 dark:text-gray-400" />
                        </Tooltip>
                      )}
                    </div>
                  </div>
                ),
                selectedDisplay: (
                  <div className="flex items-center gap-1">
                    <ProviderImage
                      sizeClassName="w-8 h-6 flex-shrink-0"
                      provider={type.provider}
                      img={type.image}
                    />
                    <div className="truncate">{labelText}</div>
                  </div>
                ),
              };
            }) || []
        }
        mode="multiple"
        icon={ServerStackIcon as React.FC<{ className?: string }>}
        allowSearch
        virtualize
        showSelectAll
        value={value || []}
        setValue={setValue}
        onClose={onClose}
        placement={placement}
        className={'h-[40px]'}
        displayMaxItems={displayMaxItems}
        selectText={selectText}
        permanent={permanent}
        showInlineClear={showInlineClear}
        hideIcon={hideIcon}
        virtualizationOptions={{
          containerStyles: { maxWidth: RESOURCE_TYPE_DROPDOWN_WIDTH },
          virtuosoStyle: {
            maxWidth: RESOURCE_TYPE_DROPDOWN_WIDTH,
            width: RESOURCE_TYPE_DROPDOWN_WIDTH,
          },
        }}
      />
    </SkeletonLoader>
  );
};
